/**
 * The level ladder, on the client.
 *
 * A workspace's learners climb ONE ladder, resolved by the API
 * (LevelsService.getLadder: the awarding badge pack's levels, else the most
 * specific tier of standalone levels, never a mix — audit T13, 2026-09-26) and
 * served by GET /v1/portal/levels and in the team dashboard's `levelLadder`.
 *
 * These helpers order it and place a balance on it exactly as the API does —
 * pointsRequired, then `order` (LevelResponseDto serves an unset order as 0),
 * then id; a balance holds the LAST rung it has reached — so the portal
 * roadmap, the manager view and GET /v1/portal/levels/earned always agree.
 * Mirror of apps/api/src/modules/gamification/level-ladder.ts; the PGlite
 * case tests/unit/gamification/level-ladder.postgres-cases.ts drives both.
 */

/** The fields of a level (LevelResponseDto) the ladder is ordered by. */
export interface LadderRung {
  id: string
  pointsRequired: number
  order?: number | null
}

/** Ladder order: pointsRequired, then `order` (unset = 0), then id. */
export function compareLadderRungs(a: LadderRung, b: LadderRung): number {
  return (
    a.pointsRequired - b.pointsRequired ||
    (a.order ?? 0) - (b.order ?? 0) ||
    (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)
  )
}

/** A copy of `levels` in ladder order, lowest rung first. */
export function sortLadder<T extends LadderRung>(levels: readonly T[]): T[] {
  return [...levels].sort(compareLadderRungs)
}

/**
 * The rung a balance holds on a SORTED ladder: the last one whose threshold it
 * has reached, or null below the first rung.
 */
export function earnedRung<T extends LadderRung>(
  ladder: readonly T[],
  points: number,
): T | null {
  let held: T | null = null
  for (const rung of ladder) {
    if (rung.pointsRequired > points) break
    held = rung
  }
  return held
}

/**
 * Whether a level (as GET /v1/gamification/levels serves it) is one of a
 * pack's levels: attached to it, or — the pack being the tenant's copy of a
 * built-in one — one of the tenant's OWN levels still attached to that
 * original. The copy takes badges, not levels, so levels added on the built-in
 * pack's page keep the original's id; the API's ladder counts them as the
 * copy's (inPackLineage in apps/api/src/modules/gamification/level-ladder.ts),
 * and the pack page must list the same set (audit T13 item 2). The catalogue
 * only returns global levels and the tenant's own, so a non-empty companyId
 * means "the tenant's own".
 */
export function onPackLadder(
  level: { badgePackId: string; companyId: string },
  pack: { id: string; forkedFromPackId?: string | null },
): boolean {
  if (!level.badgePackId) return false
  if (level.badgePackId === pack.id) return true
  return (
    !!pack.forkedFromPackId &&
    level.badgePackId === pack.forkedFromPackId &&
    !!level.companyId
  )
}

/**
 * The points band a rung covers on a SORTED ladder: from its threshold up to
 * one below the next higher rung ("100–499 pts"), open-ended for the top rung
 * ("500+ pts"). A rung that shares its threshold with the next one is never
 * held (the balance holds the later rung), so it shows just the threshold.
 */
export function rungBand(ladder: readonly LadderRung[], index: number): string {
  const lower = ladder[index].pointsRequired
  const next = ladder[index + 1]
  if (!next) return `${lower}+ pts`
  if (next.pointsRequired <= lower) return `${lower} pts`
  return `${lower}–${next.pointsRequired - 1} pts`
}
